import { editor } from 'monaco-editor';
import * as monaco from 'monaco-editor';
import { createCssModel, createYamlModel } from '../lib/monacoEnv';
import AuthorMonacoProps from 'AuthorMonacoProps';

const getCourseName = ({ courseId, firmCode }: CourseAndFirm) =>
  `${firmCode ? `${firmCode}_` : ''}${courseId}`;

const getFileName = (props: AuthorMonacoProps) => {
  const { courseId, firmCode, lang } = props;
  return firmCode ? `${firmCode}_${courseId}--${lang}` : `${courseId}--${lang}`;
};

const viewStates = new Map<string, editor.ICodeEditorViewState>();

export const getEditorViewState = (props: AuthorMonacoProps) =>
  viewStates.get(getFileName(props));

export const setEditorViewState = (
  props: AuthorMonacoProps,
  state: editor.ICodeEditorViewState
) => viewStates.set(getFileName(props), state);

const models = new Map<string, monaco.editor.ITextModel>();

// The last value we pushed into each model from `props.value` (the draft
// snapshot). Used to tell apart a genuinely new external value from a stale
// snapshot of edits the model already contains. Without this, a remount during
// the async draft save would `setValue` the pre-edit draft over a model that
// already holds an unsaved structural edit (e.g. the `> #parent` add-page
// insert), silently dropping it — which later corrupts patches on reload.
const lastSyncedValues = new Map<string, string>();

export const getModel = (props: AuthorMonacoProps) =>
  models.get(getFileName(props));

export const createFileModel = (
  props: AuthorMonacoProps
): editor.ITextModel => {
  const { lang, value = '' } = props;
  const name = getFileName(props);
  const existing = models.get(name);

  if (existing) {
    updateModel(props);
    return existing;
  }

  const model =
    lang === 'yaml'
      ? createYamlModel(name, value)
      : createCssModel(name, value);

  monaco.languages.css.cssDefaults.setOptions({ validate: false });
  models.set(name, model);
  lastSyncedValues.set(name, value);

  return model;
};

export const updateModel = (props: AuthorMonacoProps) => {
  const name = getFileName(props);
  const model = getModel(props);
  if (!model || props.value == null) return;

  // If the incoming draft snapshot matches what we last synced, the model may
  // hold newer in-flight edits that haven't been persisted to the draft yet.
  // Re-applying the snapshot would clobber them — skip. Only overwrite when the
  // draft value genuinely changed externally (e.g. a server refresh).
  if (props.value === lastSyncedValues.get(name)) return;

  model.setValue(props.value);
  lastSyncedValues.set(name, props.value);
};
